import type {
  AssemblyMateDefinition,
  MateEntityRef,
} from '../../assembly/definition'
import type { AssemblyMateDocumentJSON } from '../../assembly/document'
import {
  createPuppyBodyBindingReferenceIndex,
  resolvePuppyExportedBodyBindingId,
  type PuppyBodyBindingReferenceIndex,
} from './puppyBodyBindingReferenceResolver'
import { remapPuppyAssemblyNodeReferences } from './remapPuppyAssemblyNodeReferences'
import type {
  PuppyDisplayNodeBinding,
  PuppyVisibleModelExportRoot,
} from './types'

/**
 * Mate 文档装配节点引用重映射输入。
 */
export interface PuppyAssemblyMateDocumentNodeReferenceRemapInput {
  /** 原始 Mate 文档。 */
  readonly mates: AssemblyMateDocumentJSON
  /** 旧节点 ID 到新节点 ID 的确定性映射。 */
  readonly aliasByNodeId: Readonly<Record<string, string>>
}

/**
 * Mate 文档拓扑 bodyBinding 引用重映射输入。
 */
export interface PuppyAssemblyMateDocumentBodyBindingRemapInput {
  /** 原始 Mate 文档。 */
  readonly mates: AssemblyMateDocumentJSON
  /** 最终写入离线包的显示节点绑定。 */
  readonly nodeBindings: readonly PuppyDisplayNodeBinding[]
}

//------ 离线包 bodyBinding 稳定引用复用 开始------
/**
 * 解析 Mate 实体在最终导出包中的 bodyBindingId。
 *
 * @param entity Mate 实体引用。
 * @param index 通用离线包 bodyBinding 候选索引。
 * @returns 可确定的 occurrence 级 bodyBindingId；无法确定时返回 `null`。
 */
function resolveMateEntityExportedBodyBindingId(
  entity: MateEntityRef,
  index: PuppyBodyBindingReferenceIndex,
): string | null {
  const sourceBodyBindingId = entity.topologyRef?.bodyBindingId?.trim()
  const ownerAssemblyNodeId = entity.topologyRef?.assemblyNodeId?.trim()
    || entity.assemblyNodeId.trim()
  if (!sourceBodyBindingId || !ownerAssemblyNodeId) {
    return null
  }

  // Mate 的 topologyRef 与几何快照可能分别持有选择层、Worker 层对象 ID，统一作为消歧上下文。
  return resolvePuppyExportedBodyBindingId(index, {
    assemblyNodeId: ownerAssemblyNodeId,
    bodyBindingId: sourceBodyBindingId,
    bodyObjectIds: [
      entity.topologyRef?.bodyObjectId,
      entity.geometry.bodyObjectId,
    ],
  })
}
//------ 离线包 bodyBinding 稳定引用复用 结束------

/**
 * 重映射 Mate 实体中的 topologyRef.bodyBindingId。
 *
 * @param entity Mate 实体引用。
 * @param index 候选索引。
 * @returns 使用最终导出 bodyBindingId 的 Mate 实体引用。
 */
function remapMateEntityBodyBindingReference(
  entity: MateEntityRef,
  index: PuppyBodyBindingReferenceIndex,
): MateEntityRef {
  const exportedBodyBindingId = resolveMateEntityExportedBodyBindingId(entity, index)
  if (
    !exportedBodyBindingId
    || !entity.topologyRef
    || exportedBodyBindingId === entity.topologyRef.bodyBindingId
  ) {
    return entity
  }

  return {
    ...entity,
    topologyRef: {
      ...entity.topologyRef,
      bodyBindingId: exportedBodyBindingId,
    },
  }
}

/**
 * 重映射 Mate Definition 中所有可选实体的 bodyBindingId。
 *
 * @param mate Mate Definition。
 * @param index 候选索引。
 * @returns 使用最终导出 bodyBindingId 的 Mate Definition。
 */
function remapMateDefinitionBodyBindingReferences(
  mate: AssemblyMateDefinition,
  index: PuppyBodyBindingReferenceIndex,
): AssemblyMateDefinition {
  const firstEntity = remapMateEntityBodyBindingReference(mate.firstEntity, index)
  const secondEntity = remapMateEntityBodyBindingReference(mate.secondEntity, index)
  const referenceEntity = mate.referenceEntity
    ? remapMateEntityBodyBindingReference(mate.referenceEntity, index)
    : undefined

  return {
    ...mate,
    firstEntity,
    secondEntity,
    ...(referenceEntity ? { referenceEntity } : {}),
  }
}

/**
 * 重映射 Mate 文档中的装配节点引用。
 *
 * @param input 重映射输入。
 * @returns 使用目标节点 ID 的 Mate 文档。
 */
export function remapPuppyAssemblyMateDocumentNodeReferences(
  input: PuppyAssemblyMateDocumentNodeReferenceRemapInput,
): AssemblyMateDocumentJSON {
  return remapPuppyAssemblyNodeReferences({
    value: input.mates,
    aliasByNodeId: input.aliasByNodeId,
  })
}

/**
 * 将 Mate 拓扑引用中的 bodyBindingId 同步为最终写入离线包的 occurrence 级 bodyBindingId。
 *
 * @param input 重映射输入。
 * @returns 使用最终导出 bodyBindingId 的 Mate 文档。
 */
export function remapPuppyAssemblyMateDocumentBodyBindingReferences(
  input: PuppyAssemblyMateDocumentBodyBindingRemapInput,
): AssemblyMateDocumentJSON {
  const index = createPuppyBodyBindingReferenceIndex(input.nodeBindings)
  return {
    ...input.mates,
    mates: input.mates.mates.map((mate) => remapMateDefinitionBodyBindingReferences(mate, index)),
  }
}

/**
 * 将导出根 Mate 文档中的拓扑 bodyBinding 引用同步到最终 display/body/topology 绑定。
 *
 * @param exportRoot 已完成可见模型导出与语义源应用的离线包根。
 * @returns Mate topologyRef 使用最终 occurrence bodyBindingId 的离线包根。
 */
export function remapPuppyVisibleModelExportRootMateBodyBindingReferences(
  exportRoot: PuppyVisibleModelExportRoot,
): PuppyVisibleModelExportRoot {
  return {
    ...exportRoot,
    assemblyDocument: {
      ...exportRoot.assemblyDocument,
      mates: remapPuppyAssemblyMateDocumentBodyBindingReferences({
        mates: exportRoot.assemblyDocument.mates,
        nodeBindings: exportRoot.nodeBindings,
      }),
    },
  }
}
